import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { Root } from '@radix-ui/react-label';
import uniqueId from 'lodash/fp/uniqueId';
import {
  Children,
  ComponentPropsWithoutRef,
  forwardRef,
  isValidElement,
  ReactNode,
  useMemo,
} from 'react';
import styled from 'styled-components';

import ErrorProps from 'design-system/types/ErrorProps';

import useFormField, { FormControlContext, FormControlContextProps } from './useFormField';

const LabelBase = styled(Root)`
  ${Typography.TextMedium};
  color: ${({ theme }) => theme.color.text.primary};
`;

export const Label = (props: ComponentPropsWithoutRef<typeof Root>) => {
  const field = useFormField({ labelId: props.id });
  return <LabelBase htmlFor={field.inputId} id={field.labelId} {...props} />;
};

const HelperTextBase = styled.div<ErrorProps>`
  ${Typography.TextSmall};
  line-height: 1;
  color: ${({ theme, hasWarning, hasError }) =>
    hasError
      ? theme.color.variants.danger.default
      : hasWarning
        ? Color.CoreOrange900
        : theme.color.text.secondary};
`;

interface HelperTextProps extends ComponentPropsWithoutRef<'div'>, ErrorProps {}

export const HelperText = forwardRef<HTMLDivElement, HelperTextProps>(
  function HelperText(props, ref) {
    const field = useFormField({ helperTextId: props.id });
    return (
      <HelperTextBase
        hasError={field.hasError}
        hasWarning={field.hasWarning}
        id={field.helperTextId}
        ref={ref}
        {...props}
      />
    );
  },
);

export const ErrorText = forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  function ErrorText(props, ref) {
    const field = useFormField({ errorTextId: props.id });
    return <HelperTextBase hasError id={field.errorTextId} ref={ref} {...props} />;
  },
);

export const WarningText = forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  function WarningText(props, ref) {
    const field = useFormField({ warningTextId: props.id });
    return <HelperTextBase hasWarning id={field.warningTextId} ref={ref} {...props} />;
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${Spacing[3]};

  > *:not(:first-child) {
    margin-top: ${Spacing[2]};
  }

  > ${HelperTextBase} {
    margin-top: ${Spacing[1]};
  }
`;

export interface FormControlBaseProps extends ErrorProps {
  inputId?: string;
  disabled?: boolean;
}

export interface FormControlProps extends ComponentPropsWithoutRef<'div'>, FormControlBaseProps {}

const FormControl = forwardRef<HTMLDivElement, FormControlProps>(function FormControl(
  { children, hasError, hasWarning, inputId, disabled, ...props }: FormControlProps,
  ref,
) {
  const generatedId = useMemo(() => uniqueId('pds_form_control__'), []);

  const initFormField: FormControlContextProps = {
    hasError,
    hasWarning,
    disabled,
    inputId: inputId ?? generatedId,
  };

  Children.map(children, (child: ReactNode) => {
    if (isValidElement(child)) {
      switch (child.type) {
        case Label: {
          initFormField.labelId = child.props.id ?? `${initFormField.inputId}-label`;
          break;
        }
        case HelperText: {
          initFormField.helperTextId = child.props.id ?? `${initFormField.inputId}-help-text`;
          break;
        }
        case ErrorText: {
          initFormField.errorTextId = child.props.id ?? `${initFormField.inputId}-error-text`;
          break;
        }
        case WarningText: {
          initFormField.warningTextId = child.props.id ?? `${initFormField.inputId}-warning-text`;
          break;
        }
      }
    }
  });

  const field = useFormField(initFormField);

  return (
    <FormControlContext.Provider value={field}>
      <Container ref={ref} {...props}>
        {children}
      </Container>
    </FormControlContext.Provider>
  );
});

export default FormControl;
